<template>
  <settings-page-card>
    <div class="flex items-center justify-between px-6">
      <h3 class="text-2xl font-semibold mb-0">
        Update Role
      </h3>
    </div>

    <div class="flex flex-col mt-8 px-6">
      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <b-col>
            <b-form-group label="Name Your Role">
              <b-input
                v-model="name"
                :disabled="loading"
                :state="validationState('name')"
                placeholder="Role name"
                @blur="$v.name.$touch()"
              />
              <b-form-invalid-feedback v-if="$v.name.$error">
                <span v-if="!$v.name.required">Role name is required.</span>
                <span v-else-if="!$v.name.minLength">Role name must be at least 2 characters.</span>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Permissions:">
          <div class="flex flex-col px-[24px] py-[16px] overflow-y-auto rounded border border-[#E2E5EC]">
            <div
              v-for="(module, mIndex) in permissionList"
              :key="`${module.key}-${mIndex}`"
              class="flex flex-col mb-2"
            >
              <b-form-checkbox
                v-model="selected"
                :value="module.key"
                :disabled="loading"
                @change="onModuleToggle(module); $v.selected.$touch()"
              >
                {{ module.label }}
              </b-form-checkbox>

              <b-collapse
                :visible="selected.includes(module.key)"
                class="visible mt-1"
              >
                <div
                  v-if="module.categories && module.categories.length"
                  class="flex flex-col gap-y-[4px] ml-4"
                >
                  <div
                    v-for="cat in module.categories"
                    :key="`${module.key}.${cat.key}`"
                    class="flex flex-col gap-y-[4px]"
                  >
                    <b-form-checkbox
                      v-model="selected"
                      :value="`${module.key}.${cat.key}`"
                      :disabled="loading"
                      @change="onCategoryToggle(module, cat); $v.selected.$touch()"
                    >
                      {{ cat.label }}
                    </b-form-checkbox>

                    <div class="flex flex-col gap-y-[4px] ml-4">
                      <b-form-checkbox
                        v-for="action in cat.permissions"
                        :key="`${module.key}.${cat.key}.${action}`"
                        v-model="selected"
                        :value="`${module.key}.${cat.key}.${action}`"
                        :disabled="loading"
                        @change="onActionToggle(module); $v.selected.$touch()"
                      >
                        {{ actionLabel(action) }}
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="flex flex-col gap-y-[4px] ml-4"
                >
                  <b-form-checkbox
                    v-for="action in module.permissions"
                    :key="`${module.key}.${action}`"
                    v-model="selected"
                    :value="`${module.key}.${action}`"
                    :disabled="loading"
                    @change="onActionToggle(module); $v.selected.$touch()"
                  >
                    {{ actionLabel(action) }}
                  </b-form-checkbox>
                </div>

                <hr v-if="mIndex !== permissionList.length - 1">
              </b-collapse>
            </div>
          </div>
          <div
            v-if="$v.selected.$error"
            class="text-sm text-red-600 mt-2"
          >
            {{ permissionsErrorMessage() }}
          </div>
        </b-form-group>

        <div class="flex items-center justify-end">
          <b-btn
            variant="primary"
            class="!bg-[#4F83E3] hover:!bg-[#3967d7]"
            type="submit"
            :disabled="isSubmitDisabled"
          >
            Update Role
          </b-btn>
        </div>
      </b-form>
    </div>
  </settings-page-card>
</template>

<script>
import SettingsPageCard from '@/layouts/dashboard/SettingsPageCard.vue'
import {mapActions, mapState} from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'

export default {
  name: 'CreateRole',
  components: {SettingsPageCard},
  data () {
    return {
      name: '',
      selected: []
    }
  },
  computed: {
    ...mapState('roles', {
      loading: state => state.loading,
      permissions: state => state.permissions || {}
    }),
    permissionList () {
      const src = this.permissions || {}
      const list = Array.isArray(src) ? src : Object.values(src)

      const seen = new Set()
      return list.filter(m => {
        const k = m && m.key ? String(m.key) : ''
        if (!k) return false
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
    },
    isSubmitDisabled () {
      return this.loading || this.$v.$invalid
    }
  },
  validations: {
    name: {
      required,
      minLength: minLength(2)
    },
    selected: {
      required
    }
  },
  methods: {
    ...mapActions({
      getPermissions: 'roles/getPermissions',
      getRole: 'roles/getRole',
      updateRole: 'roles/updateRole'
    }),
    validationState (field) {
      const v = this.$v[field]
      if (!v) return null
      if (!v.$dirty) return null
      return !v.$invalid
    },
    permissionsErrorMessage () {
      if (!this.$v.selected.$dirty) return ''
      return this.$v.selected.required === false ? 'Please select at least 1 permission.' : ''
    },
    actionLabel (action) {
      if (!action) return ''
      return action.charAt(0).toUpperCase() + action.slice(1)
    },
    addToSelected (values) {
      const set = new Set(this.selected)
      values.forEach(v => set.add(v))
      this.selected = Array.from(set)
    },
    removeFromSelected (values) {
      const remove = new Set(values)
      this.selected = this.selected.filter(v => !remove.has(v))
    },
    categoryValues (module, cat) {
      const base = `${module.key}.${cat.key}`
      const actions = Array.isArray(cat.permissions) ? cat.permissions : []
      return [base, ...actions.map(a => `${base}.${a}`)]
    },
    moduleValues (module) {
      const out = [module.key]

      if (Array.isArray(module.categories) && module.categories.length) {
        module.categories.forEach(cat => {
          out.push(...this.categoryValues(module, cat))
        })
        return out
      }

      const actions = Array.isArray(module.permissions) ? module.permissions : []
      actions.forEach(a => out.push(`${module.key}.${a}`))
      return out
    },
    onModuleToggle (module) {
      this.$nextTick(() => {
        const checked = this.selected.includes(module.key)
        const values = this.moduleValues(module)
        if (checked) this.addToSelected(values)
        else this.removeFromSelected(values)
      })
    },
    onCategoryToggle (module, cat) {
      this.$nextTick(() => {
        const catKey = `${module.key}.${cat.key}`
        const checked = this.selected.includes(catKey)
        const values = this.categoryValues(module, cat)

        if (checked) {
          this.addToSelected([module.key, ...values])
        } else {
          this.removeFromSelected(values)
          this.syncModuleParent(module)
        }
      })
    },
    onActionToggle (module) {
      this.$nextTick(() => {
        this.enforceReadDependencies(module)
        this.syncModuleParent(module)
      })
    },
    enforceReadDependencies (module) {
      const crud = new Set(['create', 'update', 'delete'])

      if (Array.isArray(module.categories) && module.categories.length) {
        module.categories.forEach(cat => {
          const base = `${module.key}.${cat.key}`
          const actions = Array.isArray(cat.permissions) ? cat.permissions : []

          if (!actions.includes('read')) return

          const readKey = `${base}.read`
          const crudKeys = actions
            .filter(a => crud.has(a))
            .map(a => `${base}.${a}`)

          const anyCrudSelected = crudKeys.some(k => this.selected.includes(k))

          if (anyCrudSelected && !this.selected.includes(readKey)) {
            this.addToSelected([readKey])
          }
        })

        return
      }

      const actions = Array.isArray(module.permissions) ? module.permissions : []
      if (!actions.includes('read')) return

      const readKey = `${module.key}.read`
      const crudKeys = actions
        .filter(a => crud.has(a))
        .map(a => `${module.key}.${a}`)

      const anyCrudSelected = crudKeys.some(k => this.selected.includes(k))

      if (anyCrudSelected && !this.selected.includes(readKey)) {
        this.addToSelected([readKey])
      }
    },
    syncModuleParent (module) {
      const moduleKey = module.key

      const all = this.moduleValues(module).filter(v => v !== moduleKey)
      const anyChildSelected = all.some(v => this.selected.includes(v))

      if (anyChildSelected) {
        if (!this.selected.includes(moduleKey)) this.addToSelected([moduleKey])
      } else {
        if (this.selected.includes(moduleKey)) this.removeFromSelected([moduleKey])
      }

      if (Array.isArray(module.categories) && module.categories.length) {
        module.categories.forEach(cat => {
          const base = `${module.key}.${cat.key}`
          const actions = Array.isArray(cat.permissions) ? cat.permissions : []
          const actionKeys = actions.map(a => `${base}.${a}`)
          const anyActionSelected = actionKeys.some(v => this.selected.includes(v))

          if (anyActionSelected) {
            if (!this.selected.includes(base)) this.addToSelected([base])
          } else {
            if (this.selected.includes(base)) this.removeFromSelected([base])
          }
        })
      }
    },
    async handleSubmit () {
      try {
        this.$v.$touch()

        if (this.$v.$invalid) {
          await this.$swal.fire({
            icon: 'warning',
            iconColor: '#4F83E3',
            title: 'Please fix the highlighted fields',
            showConfirmButton: false,
            timer: 1800
          })
          return
        }

        await this.updateRole({
          id: this.$route.params.role_id,
          body: {
            name: this.name,
            permissions: this.selected
          }
        })

        await this.$swal.fire({
          icon: 'success',
          iconColor: '#4F83E3',
          title: 'Role updated',
          text: `Saved “${this.name}” with ${this.selected.length} permission${this.selected.length === 1 ? '' : 's'}.`,
          showConfirmButton: false,
          timer: 1800
        })

        this.$v.$reset()
        setTimeout(() => this.$router.push({ name: 'settings.permissions.roles' }), 1000)
      } catch (err) {
        console.error(err)
        await this.$swal.fire({
          icon: 'error',
          iconColor: '#4F83E3',
          title: err.response?.data?.message || 'Error while updating role',
          showConfirmButton: false,
          timer: 3000
        })
      }
    }
  },
  async mounted () {
    await this.$store.dispatch('projects/hasAccessToPage', 'members.update')
    await this.getPermissions()
    const data = await this.getRole(this.$route.params.role_id)

    if (Object.keys(data).length) {
      this.name = data.name
      if (data.permissions.length) {
        this.selected = data.permissions.map(item => item.permissions_key)
      }
    }
  }
}
</script>
<style scoped>
.form-control.is-valid,
.form-control.is-invalid,
.custom-select.is-valid,
.custom-select.is-invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}
</style>
